import styles from "./list.module.css";
import dayjs from "dayjs";
import { Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, Tooltip } from "@mui/material";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import TablePaginationComponent from "../../../components/TablePagination";
import { ArrowForwardOutlined, FileDownloadOutlined, PrintOutlined } from "@mui/icons-material";
import { useReactToPrint, ReactToPrint, PrintContextConsumer } from "react-to-print";
import { ComponentToPrintList } from "./ComponentToPrintList";
import { ComponentToPrint } from "./ComponentToPrint";
import { useRef } from "react";
import { authData } from "../../../components/GetAuth";
import ServiceTag from "../../../components/ServiceTag";
import ListToolbar from "../../../components/ListToolbar/ListToolbar";
import { downloadCsv } from "../../../components/ListToolbar/csv";

export const JOB_EXPORT_COLUMNS = [
    { label: "Booking Id", value: (row) => row?.booking_id },
    { label: "Service", value: (row) => (parseInt(row?.is_delivery) === 1 ? "Shop Order" : row?.freight_type || "Sea Freight") },
    { label: "Post Code", value: (row) => row?.post_code },
    { label: "Driver Name", value: (row) => row?.driver?.name },
    { label: "Address", value: (row) => row?.pickup_details?.address },
    { label: "Contact No", value: (row) => row?.pickup_details?.phone },
    { label: "Date", value: (row) => dayjs(row?.pickup_date).format("DD/MM/YYYY") },
    { label: "Assigned Date", value: (row) => dayjs(row?.driver?.assigned_date).format("DD/MM/YYYY") },
];

// Search, date, List/Map switch and a ⋯ menu (Export CSV, Print all jobs) in one row; used by the list and map views
export const DriverJobsToolbar = ({ data, filterVars, setFilterVars, viewSwitch }) => {
    const printRef = useRef();
    const handlePrint = useReactToPrint({ content: () => printRef.current });

    return (<>
        <ListToolbar
            search={filterVars?.searchKey}
            onSearch={(value) => setFilterVars((prev) => ({ ...prev, searchKey: value }))}
            dateRange={filterVars?.date}
            onDateRange={(value) => setFilterVars((prev) => ({ ...prev, date: value }))}
            viewSwitch={viewSwitch}
            menuItems={[
                { label: "Export CSV", icon: <FileDownloadOutlined fontSize="small" />, onClick: () => downloadCsv("driver-jobs.csv", JOB_EXPORT_COLUMNS, data), disabled: !data?.length },
                { label: "Print all jobs", icon: <PrintOutlined fontSize="small" />, onClick: handlePrint, disabled: !data?.length },
            ]}
        />
        <div style={{ display: "none" }}>
            <ComponentToPrintList ref={printRef} data={data} />
        </div>
    </>);
};

const ListView = ({ filteredData, filterVars, setSelectedId, setFormOpen, setFilterVars, setViewType, page, setPage, rowsPerPage, setRowsPerPage, totalCount, viewSwitch }) => {
    const printRefs = useRef({});
    const auth = authData();

    const paginationChangePage = (event, newPage) => {
        setPage(newPage);
    }

    const paginationChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    const canAssign = auth?.user_type === 2 || auth?.permissions?.indexOf('bookings:assign-driver') > -1;

    return (<>
        <DriverJobsToolbar data={filteredData} filterVars={filterVars} setFilterVars={setFilterVars} viewSwitch={viewSwitch} />

        {/* Desktop / tablet table — hidden on narrow screens in favour of the card list below */}
        <div className={styles.TableScrollWrap}>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <StyledMainTableCell>Booking Id</StyledMainTableCell>
                            <StyledMainTableCell>Post Code</StyledMainTableCell>
                            <StyledMainTableCell>Driver Name</StyledMainTableCell>
                            <StyledMainTableCell>Address</StyledMainTableCell>
                            <StyledMainTableCell>Contact No</StyledMainTableCell>
                            <StyledMainTableCell>Date</StyledMainTableCell>
                            <StyledMainTableCell>Assigned Date</StyledMainTableCell>
                            <StyledMainTableCell align="right" style={{ width: 110 }}>Action</StyledMainTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredData.map((item) => {
                            return (<StyledMainTableRow key={item?.id}>
                                <TableCell component="th" scope="row"><span className={styles.MonoCellStrong}>{item?.booking_id}</span><ServiceTag booking={item} /></TableCell>
                                <TableCell component="th" scope="row"><span className={styles.MonoCell}>{item?.post_code}</span></TableCell>
                                <TableCell component="th" scope="row">{item?.driver?.name}</TableCell>
                                <TableCell component="th" scope="row">{item?.pickup_details?.address}</TableCell>
                                <TableCell component="th" scope="row">{item?.pickup_details?.phone}</TableCell>
                                <TableCell component="th" scope="row"><span className={styles.MonoCell}>{dayjs(item?.pickup_date).format("DD/MM/YYYY")}</span></TableCell>
                                <TableCell component="th" scope="row"><span className={styles.MonoCell}>{dayjs(item?.driver?.assigned_date).format("DD/MM/YYYY")}</span></TableCell>
                                <TableCell component="th" scope="row">
                                    <div className={styles.RowActions}>
                                        <div style={{ display: "none" }}>
                                            <ComponentToPrint ref={(el) => (printRefs.current[item?.id] = el)} bookingData={filteredData} driver_id={item?.driver?.id} />
                                        </div>
                                        <ReactToPrint content={() => printRefs.current[item?.id]}>
                                            <PrintContextConsumer>
                                                {({ handlePrint }) => (<Tooltip title="Print"><Button className={styles.ActionBU} onClick={handlePrint}><PrintOutlined /></Button></Tooltip>)}
                                            </PrintContextConsumer>
                                        </ReactToPrint>
                                        {canAssign && <Tooltip title="Re-Assign or Unassign">
                                            <Button className={styles.ActionBU} onClick={(e) => { setFormOpen(true); setSelectedId(item?.id); }}><ArrowForwardOutlined /></Button>
                                        </Tooltip>}
                                    </div>
                                </TableCell>
                            </StyledMainTableRow>);
                        })}

                        {filteredData.length === 0 && (<TableRow>
                            <TableCell colSpan={10} align="center">No data found</TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredData?.length > 0 && (<div className={styles.PaginationWrap}>
                <TablePaginationComponent
                    count={totalCount}
                    colSpan={6}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={paginationChangePage}
                    handleChangeRowsPerPage={paginationChangeRowsPerPage}
                />
            </div>)}
        </div>

        {/* Mobile card list — same data, shown only on small screens (see @media 768px in list.module.css) */}
        <div className={styles.CardList}>
            {filteredData.map((item) => (
                <div className={styles.DataCard} key={item?.id}>
                    <div className={styles.DataCardTop}>
                        <span className={styles.DataCardId}>{item?.booking_id}</span><ServiceTag booking={item} />
                        <span className={styles.DataCardPostCode}>{item?.post_code}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Driver</span>
                        <span className={styles.DataCardValue}>{item?.driver?.name || "—"}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Address</span>
                        <span className={styles.DataCardValue}>{item?.pickup_details?.address}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Contact</span>
                        <span className={styles.DataCardValue}>{item?.pickup_details?.phone}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Date</span>
                        <span className={styles.DataCardValue}>{dayjs(item?.pickup_date).format("DD/MM/YYYY")}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Assigned date</span>
                        <span className={styles.DataCardValue}>{dayjs(item?.driver?.assigned_date).format("DD/MM/YYYY")}</span>
                    </div>

                    <div className={styles.DataCardActions}>
                        <div style={{ display: "none" }}>
                            <ComponentToPrint ref={(el) => (printRefs.current[`m-${item?.id}`] = el)} bookingData={filteredData} driver_id={item?.driver?.id} />
                        </div>
                        <ReactToPrint content={() => printRefs.current[`m-${item?.id}`]}>
                            <PrintContextConsumer>
                                {({ handlePrint }) => (<Button className={styles.ActionBU} onClick={handlePrint}><PrintOutlined /></Button>)}
                            </PrintContextConsumer>
                        </ReactToPrint>
                        {canAssign && (
                            <Button className={styles.ActionBU} onClick={(e) => { setFormOpen(true); setSelectedId(item?.id); }}><ArrowForwardOutlined /></Button>
                        )}
                    </div>
                </div>
            ))}

            {filteredData.length === 0 && (
                <div className={styles.EmptyState}>No data found</div>
            )}

            {filteredData?.length > 0 && (
                <TablePaginationComponent
                    count={totalCount}
                    colSpan={6}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={paginationChangePage}
                    handleChangeRowsPerPage={paginationChangeRowsPerPage}
                />
            )}
        </div>
    </>);
};

export default ListView;